import {KIDSPLAZA_V2_ASSETS_BASE_URL} from '@/lib/constants';
import {Customer, ReviewMedia} from '__generated__/__types__';
import Resizer from 'react-image-file-resizer';
import {ParsedUrlQuery} from 'querystring';
import {isArray} from 'lodash';

const MAX_SIZE_IMAGE_UPLOAD = 2 * 1024 * 1024;
const MAX_WIDTH_IMAGE_UPLOAD = 800;
const MAX_HEIGHT_IMAGE_UPLOAD = 800;

export const formatPrice = (price: number = 0) => {
    const addCommas = (nStr: string) => {
        nStr += '';
        const x = nStr.split('.');
        let x1 = x[0];
        const x2 = x.length > 1 ? '.' + x[1] : '';
        const rgx = /(\d+)(\d{3})/;
        while (rgx.test(x1)) {
            x1 = x1.replace(rgx, '$1' + '.' + '$2');
        }
        return x1 + x2;
    };
    let value = price.toString().replace(/\,/g, '');
    while (value.length > 1 && value[0] == '0' && value[1] != '.') value = value.substring(1);
    if (value != '' && value != '0') {
        if (value[value.length - 1] != '.') {
            if (value.indexOf('.000') > 0) value = value.substring(0, value.length - 3);
            value = addCommas(value);
            return value;
        } else return value;
    } else return 0;
};

export function getKeyByValue(object: any, value: any) {
    return Object.keys(object).find(key => object[key] === value);
}

export const getFirstAndLastNameFromFullName = (fullName: string): [string, string] => {
    if (!fullName) {
        return ['Tên', 'Ba Mẹ'];
    }
    const trim = (str: string) => str.replace(/^\s+|\s+$/g, '');
    fullName = trim(fullName);
    // Count words
    const words = fullName.split(' ');
    if (words.length < 2) return [fullName, fullName];
    const firstName = words[0];
    const lastName = words.slice(1).join(' ');
    return [firstName, lastName];
};

export const getFullName = (customer: Customer) => {
    return `${customer?.firstname} ${customer?.lastname}`;
};

export const getS3ImageUrl = (path: string): string => {
    return `https://media-staging.kidsplaza.store/review/${path}`;
};

export const getReviewImage = (image: ReviewMedia) => {
    const imageUrl = image?.key?.startsWith('Review/images') ? image?.url : getS3ImageUrl(image?.key);
    return imageUrl;
};

export function generateTableOfContent(htmlString: string, depth: number): string {
    // Get the headings down to the specified depth
    const pattern = new RegExp(`<h([2-${depth}])[^>]*>.*?<\/h[2-${depth}]>`, 'g');
    const winners = htmlString.match(pattern) || [];

    // Reformat the results to be more usable
    let heads = winners.join('\n');
    // heads = heads.replace('<a name="', '<a href="#');
    heads = heads.replaceAll('</a>', '');

    // Build the regular expression pattern for opening and closing tags
    const openTagPattern = new RegExp(`<h([1-${depth}])>`, 'g');
    const closeTagPattern = new RegExp(`</h[1-${depth}]>`, 'g');

    heads = heads.replace(
        openTagPattern,
        '<li class="toc$1 transition-colors cursor-pointer hover:text-primary hover:underline">',
    );
    heads = heads.replace(closeTagPattern, '</a></li>');

    // Plug the results into appropriate HTML tags
    if (heads.length > 0) {
        const contents = `<div id="toc"><p id="toc-header">Nội dung chính</p><ul class="toc-list">${heads}</ul></div>`;
        return contents;
    }
    return '';
}

// Format number to number with 1 digits after comma
export const formatThousandNumber = (number: number) => {
    if (number < 1000) {
        return number;
    }
    return `${(number / 1000).toFixed(1)}k`;
};

export const createLeadingSlashUrl = (url: string) => {
    if (url?.startsWith('/')) return url;
    return `/${url}`;
};

export const resizeImage = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
        const quality = 100;

        const resize = () => {
            Resizer.imageFileResizer(
                file,
                MAX_WIDTH_IMAGE_UPLOAD,
                MAX_HEIGHT_IMAGE_UPLOAD,
                'JPEG',
                quality,
                0,
                uri => {
                    if (typeof uri === 'string') {
                        const blob = dataURLtoBlob(uri);
                        if ((blob && blob.size <= MAX_SIZE_IMAGE_UPLOAD) || quality <= 0) {
                            const fileName = file?.name;
                            const resizedFile = new File([blob as Blob], fileName, {type: 'image/jpeg'});
                            resolve(resizedFile);
                        } else {
                            reject(new Error('Failed to convert data URL to blob.'));
                        }
                    } else {
                        reject(new Error('URI is not a string.'));
                    }
                },
                'base64',
            );
        };

        resize();
    });
};

export const dataURLtoBlob = (dataURL: string): Blob | null => {
    const arr = dataURL.split(',');
    if (arr.length < 2) {
        return null;
    }
    const matchResult = /:(.*?);/.exec(arr?.[0]);
    const mime = matchResult ? matchResult[1] : null;

    if (!mime) {
        return null;
    }
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], {type: mime});
};

export const getLastStringAfterSlash = (url: string) => {
    const segments = url?.split('/');

    if (!segments) return undefined;
    return segments[segments.length - 1];
};

export const getFirstCharOfFirstName = (fullname: string = '') => {
    const names = fullname.trim().split(/\s+/);

    const lastName = names.at(-1);
    return lastName ? lastName.charAt(0).toUpperCase() : '';
};

export const getImageAssetUrl = (path: string): string => {
    return `${KIDSPLAZA_V2_ASSETS_BASE_URL}/${path}`;
};

export const getBrandUrl = (url: string) => {
    const brandName = getLastStringAfterSlash(url);
    if (!brandName) return 'javascript:;';

    return '/' + brandName;
};

export const padSingleDigitNumberWithZero = (num: number): string => {
    return num < 10 ? `0${num}` : `${num}`;
};

export const getStringValueFromQueryByKey = (query: ParsedUrlQuery, key: string) => {
    const value = query[key];
    if (isArray(value)) {
        return value.join(', ').toString();
    }
    return value;
};
